/**
 * SurfaceStrip — `.pl`, the horizontal rail of peer cards.
 *
 * Built to `publisher-drop-hub.html` lines 1270-1310. This is the shape a set
 * of PEERS takes: the modes strip, the featured strip, the commerce rail and
 * the journey rail are all this component at the same footprint, which is the
 * anchor's point about activations taking positions rather than getting a
 * region of their own.
 *
 * ## A rail, not a grid, and that is a reading decision
 *
 * `.pl__s` scrolls horizontally with `scroll-snap-type: x proximity`. As a grid
 * the same cards wrap into rows and stop reading as a SET — the viewer scans
 * down a table instead of along a shelf. It also means any number of items sits
 * correctly: three modes or eleven, the strip is the same height and the
 * overflow is the affordance.
 *
 * The strip must be full width, below the band rather than inside the middle
 * column. Inside that column the anchor found it clipped by the leaderboard's
 * edge — a horizontal scroller in a narrow cell slices the next card through
 * its title, which reads as broken rather than as scrollable.
 *
 * ## The header's right-hand note is a summary, never a label
 *
 * `.pl__n` is where the anchor puts "Three modes · two beta windows before
 * launch" — a sentence about the set, sitting opposite the kicker. It is
 * optional, and a strip with nothing true to say there simply has a kicker.
 */

import React from "react";
import {safeCssUrl} from "@/widgets/play/safeHref";

export interface SurfaceStripCard {
    id: string;
    /** Top-right chip — the anchor's "Campaign" / "Multiplayer" / "DMZ". */
    kicker?: string;
    title: string;
    description?: string;
    /**
     * Small-caps line under the description.
     *
     * A NODE, not a string. It was `string`, which meant the roster rail had
     * nowhere to put a member's declared fields — a class year, a role, a
     * scholarship tier are drawn per TYPE, not concatenated into a sentence.
     * Widening is backward-compatible: every existing caller passes a string,
     * and a string is a valid node. It renders straight into a span with no
     * string operations, so nothing downstream had to change.
     */
    meta?: React.ReactNode;
    imageUrl?: string;
    /**
     * THE ARTWORK IS THE CARD.
     *
     * A finished creative already carries the name, the location and the
     * stats, composed at its own ratio. Cropping it to the card's shape cuts
     * the stat plate off the bottom, and printing our title over it says
     * everything twice. So: show it whole, add nothing.
     */
    artOnly?: boolean;
    /**
     * Top-left badge. `live` is red, `next` takes the accent, `soon` is a plain
     * outline — three states rather than a boolean, because "on now", "up next"
     * and "not yet" are three different things to a viewer and collapsing them
     * loses the one that drives action.
     */
    badge?: { label: string; kind?: "live" | "next" | "soon" };
    href?: string;
    onClick?: () => void;
}

export interface SurfaceStripProps {
    /**
     * A DECK, not a strip: five across on a wide screen, a snapping
     * one-thumb-per-card row below it. For finished card art where comparison
     * is the point and fifteen cards is a hand rather than a shelf.
     */
    deck?: boolean;
    /** Accent kicker on the left of the header. */
    kicker?: string;
    /** Summary opposite it. See above — a sentence, not a second label. */
    note?: string;
    cards: readonly SurfaceStripCard[];
    /**
     * The cards' titles are written SENTENCES rather than names.
     *
     * `.sh-gc__t` is display type sized for "Campaign" and "Warzone" — two
     * words that earn 19px condensed caps. A published headline is sixty
     * characters, so at that size it wraps to four lines, climbs off the scrim
     * onto the bright part of the key art, and the last line clips. That is not
     * a headline set badly, it is the wrong scale for the content.
     *
     * The anchor makes the same call for its longer commerce titles
     * (`.gc--buy .gc__t` steps down to 16px), so this is that precedent applied
     * to the one other card whose title is prose.
     */
    headline?: boolean;
    /**
     * The section's own heading, at display scale — the treatment every other
     * content section uses (`earning__title`: 24px, 32px from 768px,
     * 900-weight display caps). `kicker` is the 11px accent eyebrow, which is
     * a different job: a strip inside a section, not the section's name.
     *
     * A band that IS a section of the page passes `title`. One that sits
     * inside another section passes `kicker`. The roster passed `kicker`
     * while its siblings passed a title, which is why it read as a different
     * design rather than the same one.
     */
    title?: string;
}

const BADGE_CLASS: Record<string, string> = {
    live: "sh-gc__lv",
    next: "sh-gc__lv sh-gc__lv sh-nx",
    soon: "sh-gc__lv sh-sn",
};

export const SurfaceStrip: React.FC<SurfaceStripProps> = ({kicker, note, cards, headline, deck, title}) => {
    /* Nothing to show is not an empty strip — it is no strip. */
    if (cards.length === 0) return null;

    return (
        <section className={headline ? "sh-pl sh-pl--hl" : "sh-pl"}>
            {(title || kicker || note) &&
                <div className="sh-pl__h">
                    {title
                        ? <h3 className="sh-pl__t">{title}</h3>
                        : kicker && <span className="sh-pl__k">{kicker}</span>}
                    {note && <span className="sh-pl__n">{note}</span>}
                </div>}

            <div className={deck ? "sh-pl__s sh-pl__s--deck" : "sh-pl__s"}>
                {cards.map(card => {
                    const body = card.artOnly
                        ? <span className="sh-gc__bg" style={{backgroundImage: safeCssUrl(card.imageUrl ?? "")}} />
                        : <>
                            {card.imageUrl &&
                                <span className="sh-gc__bg" style={{backgroundImage: safeCssUrl(card.imageUrl)}} />}
                            <span className="sh-gc__sc" />
                            {card.badge &&
                                <span className={BADGE_CLASS[card.badge.kind ?? "soon"]}>{card.badge.label}</span>}
                            {card.kicker && <span className="sh-gc__tp">{card.kicker}</span>}
                            <span className="sh-gc__c">
                                <span className="sh-gc__t">{card.title}</span>
                                {card.description && <span className="sh-gc__d">{card.description}</span>}
                                {card.meta && <span className="sh-gc__m">{card.meta}</span>}
                            </span>
                        </>;
                    const cardClass = card.artOnly ? "sh-gc sh-gc--art" : "sh-gc";
                    if (card.href) {
                        return (
                            <a className={cardClass} key={card.id} href={card.href} target="_blank" rel="noreferrer">
                                {body}
                            </a>
                        );
                    }
                    if (card.onClick) {
                        return (
                            <button type="button" className={cardClass} key={card.id} onClick={card.onClick}>
                                {body}
                            </button>
                        );
                    }
                    /*
                     * A card with nowhere to go is a `div`. The anchor's are all
                     * `<a>` because a mock's are, but an anchor with no href is
                     * not focusable and announces a destination it does not have.
                     */
                    return <div className={cardClass} key={card.id}>{body}</div>;
                })}
            </div>
        </section>
    );
};

export default SurfaceStrip;
